/**
 * Verbose `[price-breaks]` logs: set `window.__PRICE_BREAKS_DEBUG__ = true` or
 * `localStorage.setItem("price-breaks-debug", "1")` then reload.
 * @param {...unknown} args
 */
function priceBreaksDebug(...args) {
  const on = typeof window !== "undefined" && (window.__PRICE_BREAKS_DEBUG__ === true || (typeof localStorage !== "undefined" && localStorage.getItem("price-breaks-debug") === "1"));
  if (on && typeof console !== "undefined" && console.debug) {
    console.debug("[price-breaks]", ...args);
  }
}

class PriceBreaks extends HTMLElement {
  /** @type {unknown} */
  apiPrices = null;

  /** @type {(ev: Event) => void} */
  _onVariantChanged = (ev) => {
    const variant = /** @type {{ detail?: { variant?: { id?: unknown; product_id?: unknown; productId?: unknown } } }} */ (ev).detail?.variant;
    if (!variant || !this.dataset.productId) return;
    const pid = variant.product_id ?? variant.productId;
    if (pid != null && String(pid) !== String(this.dataset.productId)) return;
    const script = this.findProductSkusScript();
    const productId = this.productIdFromSkusMappingScript(script) ?? this.productIdFromBreakDataScriptEl();
    if (!productId) return;
    this.refreshBreakTablesFromScripts(productId, String(variant.id));
  };

  get apiUrl() {
    const fromAttr = this.getAttribute("api-url");
    if (fromAttr) return fromAttr.replace(/\/$/, "");
    return "https://api.accounts.omnipackaging.com";
  }

  connectedCallback() {
    this.customerId = this.dataset.customerId;
    this.locationId = this.dataset.locationId;
    this.storeId = this.dataset.storeId;
    this.hmac = this.dataset.hmac;
    priceBreaksDebug("connected", {
      hasCustomerId: Boolean(this.customerId),
      hasLocationId: Boolean(this.locationId),
      hasStoreId: Boolean(this.storeId),
      hasHmac: Boolean(this.hmac),
      apiUrl: this.apiUrl,
    });
    document.addEventListener("variant:changed", this._onVariantChanged);
    void this.loadPricesFromApi();
  }

  disconnectedCallback() {
    document.removeEventListener("variant:changed", this._onVariantChanged);
  }

  /** Resolve current PDP variant id from `variant-picker` JSON or `?variant=` (nullable). */
  getSelectedVariantId() {
    const productInfo = this.closest("product-info");
    if (productInfo) {
      const script = productInfo.querySelector('variant-picker script[data-selected-variant]');
      const raw = script?.textContent?.trim();
      if (raw) {
        try {
          const v = JSON.parse(raw);
          if (v && v.id != null) return String(v.id);
        } catch {
          /* ignore */
        }
      }
    }
    try {
      const vid = new URLSearchParams(window.location.search).get("variant");
      if (vid) return vid;
    } catch {
      /* ignore */
    }
    return null;
  }

  /** Nearest `product-skus-*` JSON script: previous sibling, then document fallback. */
  findProductSkusScript() {
    let el = this.previousElementSibling;
    while (el) {
      if (el.tagName === "SCRIPT" && el.getAttribute("type") === "application/json" && el.id?.startsWith("product-variant-skus-mapping-")) {
        return el;
      }
      el = el.previousElementSibling;
    }
    return document.querySelector('script[type="application/json"][id^="product-variant-skus-mapping-"]');
  }

  /** @param {Element | null | undefined} scriptEl */
  parseVariantSkusMapping(scriptEl) {
    if (!scriptEl?.textContent?.trim()) return {};
    try {
      const raw = JSON.parse(scriptEl.textContent);
      if (!Object.keys(raw).length) return {};
      return raw;
    } catch {
      return {};
    }
  }

  /** @param {Element | null | undefined} scriptEl */
  parseSkus(scriptEl) {
    return Object.keys(this.parseVariantSkusMapping(scriptEl));
  }

  /** @param {Element | null | undefined} scriptEl */
  productIdFromSkusMappingScript(scriptEl) {
    const id = scriptEl?.id ?? "";
    const prefix = "product-variant-skus-mapping-";
    if (!id.startsWith(prefix)) return null;
    const rest = id.slice(prefix.length);
    return rest ? rest : null;
  }

  /** Fallback when SKU mapping script is missing: first `#price-break-data-{productId}` on the page. */
  productIdFromBreakDataScriptEl() {
    const el = document.querySelector('script[type="application/json"][id^="price-break-data-"]');
    const id = el?.id ?? "";
    const prefix = "price-break-data-";
    if (!id.startsWith(prefix)) return null;
    const rest = id.slice(prefix.length);
    return rest ? rest : null;
  }

  /** @param {string} productId */
  loadVariantBasesDecimalMap(productId) {
    const el = document.getElementById(`price-break-bases-${productId}`);
    if (!el?.textContent?.trim()) return {};
    try {
      const parsed = JSON.parse(el.textContent);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return /** @type {Record<string, number>} */ (parsed);
      }
    } catch {
      /* ignore */
    }
    return {};
  }

  /**
   * Tier value → unit price in decimal dollars (same rules as PriceBreakQtyTracker / pocketknife DTC).
   * @param {number} basePriceDecimal
   * @param {number} breakValueNum
   */
  effectiveBreakUnitDecimal(basePriceDecimal, breakValueNum) {
    const base = Number(basePriceDecimal);
    if (!Number.isFinite(base) || base <= 0) return NaN;
    if (!Number.isFinite(breakValueNum) || breakValueNum <= 0) return NaN;
    return breakValueNum;
  }

  /** Liquid `… | times: 10 | round: 2 | times: 10 }}0 %` discount label */
  formatDiscountLabelLiquid(baseDecimal, breakUnitDecimal) {
    if (!Number.isFinite(baseDecimal) || baseDecimal <= 0) return "0 %";
    if (!Number.isFinite(breakUnitDecimal)) return "0 %";
    const ratio = (baseDecimal - breakUnitDecimal) / baseDecimal;
    const step1 = ratio * 10;
    const rounded = Math.round(step1 * 100) / 100;
    const pct = rounded * 10;
    return `${pct.toFixed(2)} %`;
  }

  /** @param {number} cents */
  formatMoneyCents(cents) {
    const fmt = typeof window !== "undefined" ? window.MinimogSettings?.money_format : undefined;
    if (typeof formatMoney === "function" && fmt) {
      return formatMoney(cents, fmt);
    }
    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function" && fmt) {
      return Shopify.formatMoney(cents, fmt);
    }
    return (cents / 100).toFixed(2);
  }

  /**
   * Rebuild `.break-table` bodies from merged `#price-break-data-{productId}` + `#price-break-bases-{productId}`.
   * Mirrors `price-break-table.liquid` row rules (qty cap and break unit below base).
   * @param {string} productId
   * @param {string | null | undefined} [selectedVariantIdOverride] When omitted, uses DOM / URL (see getSelectedVariantId).
   */
  refreshBreakTablesFromScripts(productId, selectedVariantIdOverride) {
    const selectedId =
      selectedVariantIdOverride !== undefined && selectedVariantIdOverride !== null
        ? String(selectedVariantIdOverride)
        : this.getSelectedVariantId();
    const dataEl = document.getElementById(`price-break-data-${productId}`);
    if (!dataEl?.textContent?.trim()) {
      priceBreaksDebug("refreshBreakTables:skip — empty or missing #price-break-data", { productId });
      return;
    }

    /** @type {Record<string, Record<string, unknown>>} */
    let breaks = {};
    try {
      const parsed = JSON.parse(dataEl.textContent);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        breaks = parsed;
      }
    } catch (err) {
      priceBreaksDebug("refreshBreakTables:skip — JSON parse error on #price-break-data", {
        productId,
        error: String(err),
      });
      return;
    }

    const bases = this.loadVariantBasesDecimalMap(productId);
    const variantIdsFromJson = Object.keys(breaks);
    priceBreaksDebug("refreshBreakTables:start", {
      productId,
      variantKeysInJson: variantIdsFromJson,
      basesKeys: Object.keys(bases),
    });

    for (const variantId of variantIdsFromJson) {
      // Wrappers often live outside `<price-breaks>` (e.g. Shopify app block in section); use document + id.
      const wrap = document.getElementById(`${variantId}__price-breaks`);
      if (!(wrap instanceof HTMLElement)) {
        priceBreaksDebug("refreshBreakTables: no #variantId__price-breaks in document", { variantId });
        continue;
      }
      const baseDec = Number(bases[variantId]);
      const tierMap = breaks[variantId];
      if (!tierMap || typeof tierMap !== "object") {
        priceBreaksDebug("refreshBreakTables:variant skip — no tier map in JSON", { variantId, hasKey: variantId in breaks });
        continue;
      }

      const table = wrap.querySelector("table.break-table");
      const tbody = table?.querySelector("tbody");
      if (!tbody) {
        priceBreaksDebug("refreshBreakTables:variant skip — no tbody", { variantId, hasTable: Boolean(table) });
        continue;
      }

      let uomText = (wrap.dataset.uom || "").trim();
      if (!uomText) {
        const cells = tbody.querySelectorAll("tr.break-table-tr td.break-table-td");
        if (cells.length >= 2) {
          uomText = cells[1].textContent?.trim() ?? "";
        }
      }

      /** @type {{ qty: number; eff: number }[]} */
      const rows = [];
      /** @type {string[]} */
      const tierSkipReasons = [];
      for (const [qtyKey, raw] of Object.entries(tierMap)) {
        const qty = Number(qtyKey);
        if (!Number.isFinite(qty) || qty <= 0 || qty >= 999999) {
          tierSkipReasons.push(`${qtyKey}: bad qty`);
          continue;
        }
        const breakNum = typeof raw === "number" ? raw : Number.parseFloat(String(raw));
        if (!Number.isFinite(breakNum)) {
          tierSkipReasons.push(`${qtyKey}: non-numeric tier value`);
          continue;
        }
        const eff = this.effectiveBreakUnitDecimal(baseDec, breakNum);
        if (!Number.isFinite(eff) || eff >= 999999) {
          tierSkipReasons.push(`${qtyKey}: eff invalid (${eff})`);
          continue;
        }
        if (!Number.isFinite(baseDec) || baseDec <= 0) {
          tierSkipReasons.push(`${qtyKey}: base invalid (${baseDec})`);
          continue;
        }
        if (eff >= baseDec) {
          tierSkipReasons.push(`${qtyKey}: eff (${eff}) >= base (${baseDec})`);
          continue;
        }
        rows.push({ qty, eff });
      }
      rows.sort((a, b) => a.qty - b.qty);

      priceBreaksDebug("refreshBreakTables:variant", {
        variantId,
        baseDec,
        tierKeys: Object.keys(tierMap),
        rowCount: rows.length,
        tierSkipReasons,
      });
      if (rows.length === 0 && Object.keys(tierMap).length > 0) {
        console.info("[price-breaks] All tiers skipped for variant", variantId, { baseDec, tierSkipReasons });
      }

      tbody.replaceChildren();
      for (const { qty, eff } of rows) {
        const tr = document.createElement("tr");
        tr.className = "break-table-tr";
        const tdQty = document.createElement("td");
        tdQty.className = "break-table-td";
        tdQty.textContent = `${Math.floor(qty)}+`;
        const tdUom = document.createElement("td");
        tdUom.className = "break-table-td";
        tdUom.textContent = uomText;
        const tdPrice = document.createElement("td");
        tdPrice.className = "break-table-td";
        tdPrice.textContent = this.formatMoneyCents(Math.round(eff * 100));
        const tdDisc = document.createElement("td");
        tdDisc.className = "break-table-td";
        tdDisc.textContent = this.formatDiscountLabelLiquid(baseDec, eff);
        tr.append(tdQty, tdUom, tdPrice, tdDisc);
        tbody.appendChild(tr);
      }

      wrap.hidden =
        rows.length === 0 || (selectedId != null && String(variantId) !== selectedId);
    }

    const missingWrappers = variantIdsFromJson.filter((vid) => !document.getElementById(`${vid}__price-breaks`));
    if (missingWrappers.length > 0) {
      console.info("[price-breaks] No .price-breaks-table-wrapper for variant id(s) — Liquid / app block did not render a table shell (or id mismatch). Variants:", missingWrappers);
    }
  }

  /**
   * Merge GET /prices payload into `#price-break-data-{productId}` (same shape as Liquid snippet:
   * variant id -> { [quantity]: unit price number }).
   * @param {string} productId
   * @param {Record<string, string>} skuToVariantId
   * @param {unknown} apiPayload
   */
  applyApiPricesToBreakDataScript(productId, skuToVariantId, apiPayload) {
    const scriptId = `price-break-data-${productId}`;
    const dataEl = document.getElementById(scriptId);
    if (!dataEl) {
      priceBreaksDebug("applyApiPrices:skip — missing script", { scriptId });
      return;
    }

    /** @type {Record<string, Record<string, number>>} */
    let existing = {};
    if (dataEl.textContent?.trim()) {
      try {
        const parsed = JSON.parse(dataEl.textContent);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          existing = /** @type {Record<string, Record<string, number>>} */ (parsed);
        }
      } catch {
        existing = {};
      }
    }

    const prices = apiPayload && typeof apiPayload === "object" && "prices" in apiPayload ? apiPayload.prices : null;
    if (!Array.isArray(prices)) {
      console.info("[price-breaks] GET /prices payload has no `prices` array — not merging; refresh only runs after merge.", {
        productId,
        payloadKeys: apiPayload && typeof apiPayload === "object" ? Object.keys(apiPayload) : [],
      });
      return;
    }

    const basesScriptId = `price-break-bases-${productId}`;
    const basesEl = document.getElementById(basesScriptId);
    /** @type {Record<string, number>} */
    let basesMerged = {};
    if (basesEl?.textContent?.trim()) {
      try {
        const pb = JSON.parse(basesEl.textContent);
        if (pb && typeof pb === "object" && !Array.isArray(pb)) {
          basesMerged = /** @type {Record<string, number>} */ (pb);
        }
      } catch {
        basesMerged = {};
      }
    }

    const merged = { ...existing };
    for (const row of prices) {
      if (!row || typeof row !== "object") continue;
      const sku = /** @type {{ sku?: string }} */ (row).sku;
      if (!sku) continue;
      const variantId = skuToVariantId[sku];
      if (!variantId) continue;

      /** GET /prices list price (decimal dollars) — may differ from Shopify `variant.price` per UOM (e.g. carton vs bag). */
      const rawList = /** @type {{ price?: unknown }} */ (row).price;
      if (rawList != null) {
        const listDec = typeof rawList === "number" ? rawList : parseFloat(String(rawList));
        if (Number.isFinite(listDec) && listDec > 0) {
          basesMerged[String(variantId)] = listDec;
        }
      }

      const breaks = /** @type {{ priceBreaks?: unknown }} */ (row).priceBreaks;
      if (!Array.isArray(breaks)) {
        continue;
      }
      if (breaks.length === 0) {
        merged[String(variantId)] = {};
        continue;
      }

      /** @type {Record<string, number>} */
      const tierMap = {};
      for (const pb of breaks) {
        if (!pb || typeof pb !== "object") continue;
        const q = /** @type {{ quantity?: unknown }} */ (pb).quantity;
        const p = /** @type {{ price?: unknown }} */ (pb).price;
        if (q == null || p == null) continue;
        const qtyNum = Number(q);
        if (!Number.isFinite(qtyNum) || qtyNum <= 0) continue;
        const priceNum = typeof p === "number" ? p : parseFloat(String(p));
        if (!Number.isFinite(priceNum)) continue;
        tierMap[String(qtyNum)] = priceNum;
      }
      if (Object.keys(tierMap).length > 0) {
        merged[String(variantId)] = tierMap;
      }
    }

    dataEl.textContent = JSON.stringify(merged);
    if (basesEl) {
      basesEl.textContent = JSON.stringify(basesMerged);
    }
    priceBreaksDebug("applyApiPrices:merged", {
      productId,
      variantKeys: Object.keys(merged),
      skuToVariantCount: Object.keys(skuToVariantId).length,
    });
    this.refreshBreakTablesFromScripts(productId);
    document.dispatchEvent(
      new CustomEvent("price-break-data:updated", {
        bubbles: true,
        detail: { productId },
      }),
    );
  }

  /** @param {string[]} skus */
  buildPricesUrl(skus) {
    const base = `${this.apiUrl}/prices`;
    const params = new URLSearchParams();
    params.append("squash", "true");
    for (const sku of skus) {
      params.append("skus[]", sku);
    }
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  async loadPricesFromApi() {
    const script = this.findProductSkusScript();
    const skus = this.parseSkus(script);
    const mappingProductId = this.productIdFromSkusMappingScript(script);
    /** Apply selected-variant + empty-table visibility before /prices returns (was only in `finally`, causing duplicate tables during fetch). */
    const productIdForImmediateRefresh = mappingProductId ?? this.productIdFromBreakDataScriptEl();
    if (productIdForImmediateRefresh) {
      this.refreshBreakTablesFromScripts(productIdForImmediateRefresh);
    }
    priceBreaksDebug("loadPricesFromApi", {
      mappingScriptId: script?.id ?? null,
      skuCount: skus.length,
      skus,
      mappingProductId,
    });

    let url = "";
    try {
      if (skus.length === 0) {
        console.info("[price-breaks] No SKUs in product-variant-skus-mapping — skipping GET /prices.", {
          mappingScriptFound: Boolean(script),
          mappingScriptId: script?.id ?? null,
        });
      } else {
        url = this.buildPricesUrl(skus);
        const res = await fetch(url, {
          method: "GET",
          headers: {
            "X-Storefront-Customer-Id": this.customerId,
            "X-Storefront-Location-Id": this.locationId,
            "X-Storefront-Store-Id": this.storeId,
            "X-Hmac": this.hmac,
          },
        });
        if (!res.ok) {
          throw new Error(`GET /prices failed: ${res.status} ${res.statusText}`);
        }
        const contentType = res.headers.get("content-type") || "";
        this.apiPrices = contentType.includes("application/json") ? await res.json() : await res.text();

        const skuToVariantId = this.parseVariantSkusMapping(script);
        const productId = this.productIdFromSkusMappingScript(script);
        if (productId && typeof this.apiPrices === "object" && this.apiPrices !== null) {
          this.applyApiPricesToBreakDataScript(productId, skuToVariantId, this.apiPrices);
        } else {
          console.info("[price-breaks] Skipped applyApiPricesToBreakDataScript", {
            productId,
            apiPricesType: this.apiPrices === null ? "null" : typeof this.apiPrices,
          });
        }

        this.dispatchEvent(
          new CustomEvent("price-breaks:prices-loaded", {
            bubbles: true,
            detail: { skus, url, data: this.apiPrices, productId },
          }),
        );
      }
    } catch (err) {
      console.info("[price-breaks] GET /prices failed — still refreshing tables from #price-break-data in DOM.", {
        url: url || undefined,
        error: err instanceof Error ? err.message : String(err),
      });
      this.dispatchEvent(
        new CustomEvent("price-breaks:prices-error", {
          bubbles: true,
          detail: { skus, url, error: err },
        }),
      );
    } finally {
      const productId = mappingProductId ?? this.productIdFromBreakDataScriptEl();
      if (productId) {
        this.refreshBreakTablesFromScripts(productId);
      }
      priceBreaksDebug("loadPricesFromApi:finally refresh", { productId: productId ?? null });
    }
  }
}

customElements.define("price-breaks", PriceBreaks);
